import { useState, useEffect } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import MultiSearchSelect from '../../../components/Forms/Input/MultiSearchSelect';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useCreditNoteLookup } from '../../../hooks/purchases/useCreditNoteLookup';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { calculateDiscountTotal } from '../../../utils/taxCalculator';

interface Props {
  onAdd: (product: any) => void;
}

const AddDiscountProductForm = ({ onAdd }: Props) => {
  const {
    form: { supplier, id: creditNoteId },
  } = useSelector((state: RootState) => state.basePurchase);

  const {
    filteredDocuments,
    productOptions,
    searchEligiblePurchases,
    fetchProductsByPurchase,
    clearCreditNoteLookup,
  } = useCreditNoteLookup();

  const [form, setForm] = useState({
    purchaseRefId: '',
    selectedProductIds: [] as number[],
    netAmount: '',
    iva: '',
    total: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (supplier?.id) {
      searchEligiblePurchases({ supplierId: supplier.id });
    }
    return () => clearCreditNoteLookup();
  }, [supplier?.id]);

  useEffect(() => {
    const { netAmount } = form;
    if (netAmount) {
      const { iva, total } = calculateDiscountTotal(parseFloat(netAmount));
      setForm((prev) => ({
        ...prev,
        iva: iva.toFixed(2),
        total: total.toFixed(2),
      }));
    }
  }, [form.netAmount]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    const errs: typeof errors = {};
    if (!form.purchaseRefId) errs.purchaseRefId = 'CCF requerido';
    if (!form.selectedProductIds.length)
      errs.selectedProductIds = 'Productos requeridos';
    if (!form.netAmount) errs.netAmount = 'Monto neto requerido';

    if (Object.keys(errs).length) return setErrors(errs);

    const payload = {
      creditNoteId,
      purchaseReferenceId: Number(form.purchaseRefId),
      productIds: form.selectedProductIds,
      netAmount: Number(form.netAmount),
      type: 'DISCOUNT',
    };

    onAdd(payload);
    resetForm();
  };

  const resetForm = () => {
    setForm({
      purchaseRefId: '',
      selectedProductIds: [],
      netAmount: '',
      iva: '',
      total: '',
    });
    setErrors({});
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <SimpleSearchSelect
            label="Crédito Fiscal del Proveedor"
            name="purchaseRefId"
            iconKey="invoice"
            options={filteredDocuments}
            selectedValue={
              form.purchaseRefId ? Number(form.purchaseRefId) : null
            }
            onSelect={(option: any) => {
              setForm((prev) => ({
                ...prev,
                purchaseRefId: option.id,
                selectedProductIds: [],
              }));
              fetchProductsByPurchase(option.id);
            }}
            error={errors.purchaseRefId}
            required
          />
        </div>

        <div className="md:col-span-2">
          <MultiSearchSelect
            label="Productos del Crédito Fiscal que Afecta"
            name="selectedProductIds"
            iconKey="product"
            options={productOptions}
            selectedValues={form.selectedProductIds}
            onChange={(ids) =>
              setForm((prev) => ({ ...prev, selectedProductIds: ids }))
            }
            error={errors.selectedProductIds}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <InputField
          label="Monto Neto"
          name="netAmount"
          type="number"
          iconKey="dollar"
          value={form.netAmount}
          onChange={handleChange}
          error={errors.netAmount}
          required
        />
        <InputField
          label="IVA 13.00%"
          name="iva"
          type="number"
          iconKey="dollar"
          value={form.iva}
          onChange={() => {}}
          disabled
        />
        <InputField
          label="Monto Total"
          name="total"
          type="number"
          iconKey="dollar"
          value={form.total}
          onChange={() => {}}
          disabled
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Guardar
        </button>
        <button
          type="button"
          onClick={resetForm}
          className="inline-flex items-center gap-2 rounded-md bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default AddDiscountProductForm;
